import { getTranslations } from '@webapp-next/lib/i18next';
import { DateTime } from 'luxon';
import { convertHHMMToSeconds } from '@webapp-next/modules/people/composables/format-date-time';
import { formatTimeByGlobalSetting } from '@webapp-next/modules/schedule/composables/agreed-hours/formatDateTime';
import { BREAK_TYPES_LONG } from '../definitions';
import { check as checkFF } from '@webapp-next/lib/feature-flag';
import {
  WORK_TYPE,
  isNonWorkedWorkType,
} from '@webapp-next/modules/schedule/composables/worktypes';

const days = {
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
  sunday: 7,
};

export const weekOptions = {
  1: 'ONE',
  2: 'TWO',
  3: 'THREE',
  4: 'FOUR',
};

export const weekTextToNumberMapper = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
};

function generateUID() {
  return Math.random()
    .toString(36)
    .substr(2, 9);
}

function generateDefaultAgreedHoursDetailsTableData() {
  return Object.keys(days).map(dayName => ({
    isChecked: false,
    day: dayName,
    dayTotalHours: 0,
    shifts: [getDefaultAgreedHoursDetailsShift()],
  }));
}

function getDefaultAgreedHoursDetailsShift() {
  return {
    id: generateUID(),
    startTime: '',
    endTime: '',
    breaks: [getDefaultAgreedShiftBreak()],
    totalHours: 0,
    location: 0,
    area: 0,
    locationArea: '',
    errors: [],
    areaWarning: false,
  };
}

/*
  this used to be a const but now converted into a function
  because the const is evaluated before translation method is asynchronously setup
  translation setup is blocking but can't stop a import file from evaluating a const
*/
function getNoBreakType() {
  const t = getTranslations({
    module: 'people',
    file: 'people-form-tab-employment-agreed-days-times',
  });
  return {
    value: 'NO_BREAK',
    name: t('no-break'),
  };
}

const weekNumberMapper = {
  firstWeek: 'ONE',
  secondWeek: 'TWO',
  thirdWeek: 'THREE',
  fourthWeek: 'FOUR',
};

function getDefaultAgreedShiftBreak() {
  const noBreakType = getNoBreakType();
  return {
    id: generateUID(),
    breakType: noBreakType.value,
    breakDuration: '',
    startBreakTime: '',
    endBreakTime: '',
    breakErrors: [],
  };
}

function prepareAgreedHoursDetails(agreedHoursDetails = [], workplaceMap) {
  let formattedAgreedHoursDetails = [];
  formattedAgreedHoursDetails = agreedHoursDetails?.map(agreedDayTimes =>
    formatAgreedDayTimes(agreedDayTimes, workplaceMap)
  );
  return formattedAgreedHoursDetails;
}

function formatAgreedDayTimes(agreedDayTimes, workplaceMap) {
  const populateShifts = agreedDayTimes?.shifts?.map(shift =>
    populateAgreedDayShifts(shift, workplaceMap)
  );
  const dayTotalHours = calculateAgreedDayTotalHours(
    agreedDayTimes,
    workplaceMap
  );
  const dayNonWorkedHours = calculateAgreedDayNonWorkedHours(
    agreedDayTimes,
    workplaceMap
  );

  return {
    ...agreedDayTimes,
    id: generateUID(),
    isChecked: true,
    day: getKeyByValue(days, parseInt(agreedDayTimes.day)),
    shifts: populateShifts,
    dayTotalHours,
    dayNonWorkedHours,
  };
}

function getKeyByValue(object, value) {
  return Object.keys(object).find(key => object[key] === value);
}

function populateAgreedDayShifts(shift, workplaceMap) {
  const { location, area } = shift;
  const { locationName, areaName } = getLocationAndAreaNames(
    workplaceMap,
    location,
    area
  );
  const populateBreaks = shift?.breaks?.map(shiftBreak => ({
    ...shiftBreak,
    id: generateUID(),
    breakErrors: [],
  }));

  return {
    ...shift,
    id: generateUID(),
    errors: [],
    locationArea: buildAgreedDaysTimeLocationArea(locationName, areaName),
    breaks: populateBreaks || [],
  };
}

function buildAgreedDaysTimeLocationArea(locationName, areaName) {
  if (!locationName || !areaName) {
    return '';
  }
  const prepareLocationName = locationName.toUpperCase();
  return `[${prepareLocationName}] - ${areaName}`;
}

function getLocationAndAreaNames(workplaceMap, locationId, areaId) {
  let areaName = '';
  let locationName = '';
  if (!locationId || !areaId) {
    return {
      locationName,
      areaName,
    };
  }
  const location = Object.values(workplaceMap)?.find(
    workplace => workplace.id === parseInt(locationId)
  );
  if (location) {
    const { name, areas } = location;
    locationName = name;
    areaName = areas?.find(area => area.id === parseInt(areaId))?.name;
  }
  return {
    locationName,
    areaName,
  };
}

// Same rule as the schedule's scheduled-hours count: shifts in these areas
// never count towards regular working hours.
function isNonWorkedAreaShift(shift, workplaceMap) {
  const areas = workplaceMap?.[shift?.location]?.areas;
  const area = areas?.find(({ id }) => id === parseInt(shift?.area));
  return isNonWorkedWorkType(area?.workType);
}

function sumShiftHours(day, predicate) {
  return (day?.shifts || []).reduce((total, shift) => {
    return predicate(shift) ? total + Number(shift.totalHours) : total;
  }, 0);
}

function calculateAgreedDayTotalHours(day, workplaceMap) {
  return sumShiftHours(
    day,
    shift => !isNonWorkedAreaShift(shift, workplaceMap)
  );
}

function calculateAgreedDayNonWorkedHours(day, workplaceMap) {
  return sumShiftHours(day, shift => isNonWorkedAreaShift(shift, workplaceMap));
}

// For area lists that come without WorkType (e.g. listCommonAreas): look the
// type up from the OperationalUnit records for the same ids.
function withAreaWorkTypes(areas, operationalUnits) {
  const workTypeById = new Map(
    (operationalUnits || []).map(unit => [unit.Id, unit.WorkType])
  );
  return (areas || []).map(area => ({
    ...area,
    workType: workTypeById.get(area.id) || WORK_TYPE.DEFAULT,
  }));
}

function convertTimeInSecondsToDecimal(timeInSec) {
  return parseFloat(parseFloat(timeInSec / 60 / 60)).toFixed(2);
}

function convertDecimalTimeToHHMM(decimalTime) {
  const hrs = parseInt(Number(decimalTime));
  const minutes = Math.round((Number(decimalTime) - hrs) * 60);
  if (hrs > 0 && minutes > 0) {
    return `${hrs}h ${minutes}m`;
  }
  if (hrs > 0) {
    return `${hrs}h`;
  }
  if (minutes > 0) {
    return `${minutes}m`;
  }
  return 0;
}

function calculateShiftTotalBreaks(breaks = []) {
  return (breaks || []).reduce(
    ({ totalRestBreaks, totalMealBreaks }, shiftBreak) => {
      if (shiftBreak.breakType === BREAK_TYPES_LONG.MEAL) {
        totalMealBreaks += convertHHMMToSeconds(shiftBreak.breakDuration);
      }
      if (shiftBreak.breakType === BREAK_TYPES_LONG.REST) {
        totalRestBreaks += convertHHMMToSeconds(shiftBreak.breakDuration);
      }
      return { totalRestBreaks, totalMealBreaks };
    },
    {
      totalRestBreaks: 0,
      totalMealBreaks: 0,
    }
  );
}

function agreedDaysTimesPreviewFormatter(agreedDayTimes = [], workplaceMap) {
  return (agreedDayTimes || []).map(agreedDay => {
    const { day, shifts } = agreedDay;
    const dayTotalHours = calculateAgreedDayTotalHours(agreedDay, workplaceMap);
    const newShifts = shifts?.map(shift => {
      const { location, area, startTime, endTime, breaks = [] } = shift;
      const { locationName, areaName } = getLocationAndAreaNames(
        workplaceMap,
        location,
        area
      );
      const locationArea = buildAgreedDaysTimeLocationArea(
        locationName,
        areaName
      );
      const { totalRestBreaks, totalMealBreaks } = calculateShiftTotalBreaks(
        breaks
      );

      return {
        startTime: formatTimeByGlobalSetting(startTime),
        endTime: formatTimeByGlobalSetting(endTime),
        locationArea,
        totalMealBreaks: convertDecimalTimeToHHMM(
          convertTimeInSecondsToDecimal(totalMealBreaks)
        ),
        totalRestBreaks: convertDecimalTimeToHHMM(
          convertTimeInSecondsToDecimal(totalRestBreaks)
        ),
      };
    });

    return {
      day: getKeyByValue(days, parseInt(day)),
      id: generateUID(),
      shifts: newShifts,
      dayTotalHours: convertDecimalTimeToHHMM(dayTotalHours),
    };
  });
}

function sanitizeAgreedHourDetails(detailsTableData) {
  const noBreakType = getNoBreakType();
  // TODO: is this full released already?
  if (checkFF('cmpl1-1566-agreed-hours-multi-week')) {
    const sanitizedAgreedHourShifts = {};
    Object.keys(detailsTableData).forEach(week => {
      sanitizedAgreedHourShifts[weekOptions[week]] = detailsTableData[week].days
        ?.filter(detailsDay => detailsDay.isChecked)
        ?.map(singleDay => {
          const { day, shifts } = singleDay;
          const sanitizedShifts = shifts
            .filter(shift => shift.totalHours > 0)
            ?.map(shift => {
              const {
                startTime,
                endTime,
                location,
                area,
                totalHours,
                breaks,
              } = shift;
              const sanitizedBreaks = breaks?.map(shiftBreak => {
                let {
                  breakType,
                  breakDuration,
                  startBreakTime,
                  endBreakTime,
                } = shiftBreak;
                if (breakDuration === '' && breakType !== noBreakType.value) {
                  breakType = noBreakType.value;
                }
                return {
                  breakType,
                  breakDuration,
                  startBreakTime,
                  endBreakTime,
                };
              });
              return {
                startTime,
                endTime,
                breaks: sanitizedBreaks,
                location,
                area,
                totalHours,
              };
            });

          return {
            day: days[day],
            shifts: sanitizedShifts,
          };
        });
    });

    return sanitizedAgreedHourShifts;
  } else {
    return detailsTableData
      ?.filter(detailsDay => detailsDay.isChecked)
      ?.map(singleDay => {
        const { day, shifts } = singleDay;
        const sanitizedShifts = shifts
          .filter(shift => shift.totalHours > 0)
          ?.map(shift => {
            const {
              startTime,
              endTime,
              location,
              area,
              totalHours,
              breaks,
            } = shift;
            const sanitizedBreaks = breaks?.map(shiftBreak => {
              let {
                breakType,
                breakDuration,
                startBreakTime,
                endBreakTime,
              } = shiftBreak;
              if (breakDuration === '' && breakType !== noBreakType.value) {
                breakType = noBreakType.value;
              }
              return {
                breakType,
                breakDuration,
                startBreakTime,
                endBreakTime,
              };
            });
            return {
              startTime,
              endTime,
              breaks: sanitizedBreaks,
              location,
              area,
              totalHours,
            };
          });

        return {
          day: days[day],
          shifts: sanitizedShifts,
        };
      });
  }
}

function calculatePadding(shift) {
  const { breaks, errors } = shift;
  const heightDueToBreaks = breaks.length > 1 ? (breaks.length - 1) * 36 : 0;
  const breakErrorHeight = breaks.reduce((totalHeight, shiftBreak) => {
    if (shiftBreak.breakErrors.length) {
      totalHeight += 24;
    }
    return totalHeight;
  }, 0);
  const heightDueToOtherErrors =
    errors?.length > 0 && !(shift?.breaks[0]?.breakErrors?.length > 0) ? 24 : 0;
  const heightDueToAreaWarning =
    heightDueToOtherErrors === 0 && shift.areaWarning ? 24 : 0;
  const removePadding =
    (heightDueToAreaWarning > 0 || heightDueToOtherErrors > 0) &&
    shift?.breaks[0]?.breakErrors?.length > 0
      ? 24
      : 0;
  return `${heightDueToBreaks +
    breakErrorHeight +
    heightDueToOtherErrors +
    heightDueToAreaWarning -
    removePadding}px`;
}

function calculateShiftBreaksContainerPadding(shift) {
  const { errors, areaWarning } = shift;
  let paddingBottom = 0;
  if (errors.length || areaWarning) {
    paddingBottom = 24;
  }
  return `${paddingBottom}px`;
}

function calculateShiftPadding(shift, fieldErrors, isAreaSelector = false) {
  const { breaks } = shift;
  const heightDueToBreaks = breaks.length > 1 ? (breaks.length - 1) * 36 : 0;
  let breakErrorHeight = 0;
  if (breaks.length > 1) {
    breakErrorHeight = breaks.slice(1).reduce((totalHeight, shiftBreak) => {
      if (shiftBreak.breakErrors.length) {
        totalHeight += 24;
      }
      return totalHeight;
    }, 0);
  }
  const shouldAddPadding = shouldAddPaddingBottom(
    shift,
    fieldErrors,
    isAreaSelector
  );
  const heightDueToOtherErrors = shouldAddPadding ? 24 : 0;
  return `${heightDueToBreaks + breakErrorHeight + heightDueToOtherErrors}px`;
}

// Add padding if there are some errors on the shift but none of them is related to the field
function shouldAddPaddingBottom(shift, fieldErrors, isAreaSelector) {
  if (isAreaSelector && shift.areaWarning) {
    return false;
  }
  const { errors } = shift;
  if (
    !errors.length &&
    !shift.areaWarning &&
    !(shift?.breaks[0]?.breakErrors?.length > 0)
  ) {
    return false;
  }
  const currentErrors = errors.map(error => error.type);
  const shiftErrorIsRelatedToTheField = fieldErrors.some(error =>
    currentErrors.includes(error)
  );
  if (shiftErrorIsRelatedToTheField) {
    return false;
  }
  return (
    (errors.length && !shiftErrorIsRelatedToTheField) ||
    shift?.breaks[0]?.breakErrors?.length > 0 ||
    shift.areaWarning
  );
}

function getEmployeeWorkplaces(
  workplaceIds,
  areas,
  locations,
  workplacesConfig
) {
  return workplaceIds.reduce((workplaceMap, workplaceId) => {
    workplaceMap[workplaceId] = {
      id: workplaceId,
      name: locations?.find(location => location.Id === workplaceId)?.Code,
      areas: areas
        ?.filter(area => area.Company === workplaceId)
        ?.map(area => ({
          id: area.Id,
          name: area.OperationalUnitName,
          company: area.Company,
          companyName: area.CompanyName,
          trainings: area?.TrainingModule || [],
          workType: area.WorkType || WORK_TYPE.DEFAULT,
        })),
      settings: workplacesConfig[workplaceId],
    };
    return workplaceMap;
  }, {});
}

function getTimeStamp(time) {
  const [timeHour, timeMin] = time.split(':');
  return DateTime.fromObject({
    hour: parseInt(timeHour),
    minute: parseInt(timeMin),
  });
}

function getEndBreakTimestamp(breakStartTimeStamp, duration) {
  const [durationHour, durationMin] = duration.split(':');
  return breakStartTimeStamp.plus({
    hours: parseInt(durationHour),
    minutes: parseInt(durationMin),
  });
}

function getNextDayTimestamp(time) {
  const [timeHour, timeMin] = time.split(':');
  return DateTime.fromObject({
    hour: parseInt(timeHour),
    minute: parseInt(timeMin),
  }).plus({ days: 1 });
}

function getBreakStartTimestamp(shiftStart, breakStart, shiftEndTimestamp) {
  if (!breakStart || breakStart === '') {
    return;
  }
  if (breakStart < shiftStart) {
    const tempTime = getNextDayTimestamp(breakStart);
    return tempTime < shiftEndTimestamp ? tempTime : getTimeStamp(breakStart);
  }
  return getTimeStamp(breakStart);
}

function buildBreaksTimestamps(shiftStartTime, shiftEndTimestamp, breaks) {
  return breaks.map(shiftBreak => {
    const { startBreakTime, endBreakTime, breakDuration } = shiftBreak;
    if (startBreakTime === '' || endBreakTime === '' || breakDuration === '') {
      return { ...shiftBreak };
    }
    const startBreakTimestamp = getBreakStartTimestamp(
      shiftStartTime,
      startBreakTime,
      shiftEndTimestamp
    );
    const endBreakTimestamp = getEndBreakTimestamp(
      startBreakTimestamp,
      breakDuration
    );
    return {
      ...shiftBreak,
      startBreakTimestamp,
      endBreakTimestamp,
    };
  });
}

function calculateTotalHoursPerWorkPeriod(hours, cycle) {
  const mul = {
    TWO_WEEK: 2,
    FOUR_WEEK: 4,
  };
  return hours * (mul[cycle] ?? 1);
}

function sortAgreedDaysTimesTableData(tableData = [], startDay = 'monday') {
  const daysTextArray = Object.keys(days);
  let index = daysTextArray.findIndex(text => text === startDay);
  if (index < 0) {
    index = 0;
  }

  // Moves the tail end of the array to the start
  const tail = daysTextArray.splice(index);
  if (tail) {
    daysTextArray.unshift(...tail);
  }

  // Creates an object that maps day text e.g. 'wednesday' to number values for sorting later
  const daysMappedToValues = daysTextArray.reduce((reduced, day, index) => {
    reduced[day] = index;
    return reduced;
  }, {});

  return tableData.sort((firstObject, secondObject) => {
    return daysMappedToValues[firstObject.day] >
      daysMappedToValues[secondObject.day]
      ? 1
      : -1;
  });
}

function buildAgreedHourAnalyticsData(agreedHour, workplaceMap) {
  const weeks = agreedHour?.details?.weeks || [];

  const {
    totalHoursPerWorkPeriod,
    numberOfDays,
    numberOfRestBreaks,
    numberOfMealBreaks,
  } = weeks.reduce(
    (
      {
        totalHoursPerWorkPeriod,
        numberOfDays,
        numberOfRestBreaks,
        numberOfMealBreaks,
      },
      week
    ) => {
      if (week?.days) {
        week.days.reduce((reduced, { day, shifts }) => {
          totalHoursPerWorkPeriod +=
            calculateAgreedDayTotalHours({ day, shifts }, workplaceMap) || 0;

          const totalShiftBreak = shifts.map(shift => {
            const { breaks } = shift;
            return calculateShiftTotalBreaks(breaks);
          }, {});

          numberOfRestBreaks += totalShiftBreak[0].totalRestBreaks || 0;
          numberOfMealBreaks += totalShiftBreak[0].totalMealBreaks || 0;
          numberOfDays++;
        }, {});
      }

      return {
        totalHoursPerWorkPeriod,
        numberOfDays,
        numberOfRestBreaks,
        numberOfMealBreaks,
      };
    },
    {
      totalHoursPerWorkPeriod: 0,
      numberOfDays: 0,
      numberOfRestBreaks: 0,
      numberOfMealBreaks: 0,
    }
  );

  return {
    totalHoursPerWorkPeriod,
    numberOfDays,
    numberOfRestBreaks,
    numberOfMealBreaks,
  };
}

function getDateDifferenceFromNowInDays(fromDate) {
  const date = DateTime.fromJSDate(fromDate)
    .endOf('day')
    .toUTC();
  const now = DateTime.local()
    .endOf('day')
    .toUTC();
  const { days } = date.diff(now, ['days']).toObject();
  return days;
}

export {
  days,
  getNoBreakType,
  weekNumberMapper,
  generateUID,
  getDefaultAgreedShiftBreak,
  generateDefaultAgreedHoursDetailsTableData,
  getDefaultAgreedHoursDetailsShift,
  prepareAgreedHoursDetails,
  buildAgreedDaysTimeLocationArea,
  calculateAgreedDayTotalHours,
  sanitizeAgreedHourDetails,
  calculatePadding,
  calculateShiftPadding,
  calculateShiftBreaksContainerPadding,
  getEmployeeWorkplaces,
  getTimeStamp,
  getEndBreakTimestamp,
  getNextDayTimestamp,
  buildBreaksTimestamps,
  agreedDaysTimesPreviewFormatter,
  convertDecimalTimeToHHMM,
  convertTimeInSecondsToDecimal,
  calculateTotalHoursPerWorkPeriod,
  isNonWorkedAreaShift,
  calculateAgreedDayNonWorkedHours,
  withAreaWorkTypes,
  sortAgreedDaysTimesTableData,
  buildAgreedHourAnalyticsData,
  getDateDifferenceFromNowInDays,
};
